import { post, getGraphToken, fetcher, patch, del } from '../../../services/api';
import { getAccessToken } from '../../../services/authService';
import type { ChatMessage, UserMessage, ChatSession, MessageContext, DisplayContent } from '../types/domain';
import type {
  ApiChatRequest,
  ApiChatResponse,
  ApiDisplayContent,
  NavChatStreamRequest,
  NavResumeStreamRequest,
  NavStreamEvent,
  NavStreamEventHandlers,
} from '../types/api';
import { chatHandlerRegistry } from './chatHandlerRegistry';
import type { HandlerType } from '../types/handlerResults';
import { environment } from '../../../config/environment';

interface ApiThread {
  id: string;
  langgraph_thread_id: string;
  title: string | null;
  title_status: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface ApiThreadMessage {
  id: string | null;
  role: 'user' | 'assistant';
  content: string;
}

// Simple storage for sessions — capped to prevent unbounded memory growth
const MAX_IN_MEMORY_SESSIONS = 50;
let sessions: ChatSession[] = [];
let currentSessionId: string | null = null;

/**
 * Maps API panel_action values to panel IDs
 */
const mapPanelActionToTargetPanel = (panelAction: string | null | undefined): string | undefined => {
  if (!panelAction) return undefined;

  const panelActionToPanelMap: Record<string, string> = {
    switch_to_thread_summariser: 'thread-summarisation-panel',
    switch_to_diary_management: 'diary-management-panel',
    switch_to_meeting_availability: 'meeting-availability-panel',
    switch_to_action_items: 'tasks-panel',
    switch_to_my_account: 'my-account-panel',
  };

  return panelActionToPanelMap[panelAction];
};

class ChatApiService {
  async sendMessage(content: string, context?: MessageContext, userMessageId?: string): Promise<ChatMessage> {
    const session = await this.ensureSession();
    const trimmedContent = content.trim();

    this.addUserMessageToSession(session, trimmedContent, context, userMessageId);

    const handlerResult = await this.tryHandlerProcessing(session, trimmedContent, context);
    if (handlerResult) {
      return handlerResult;
    }

    return await this.processRegularChat(session, trimmedContent);
  }

  async sendWidgetResponse(
    widgetId: string,
    selectedOptions: string[],
    buttonAction?: string,
  ): Promise<{ session: ChatSession; message: ChatMessage }> {
    const session = await this.ensureSession();

    const apiRequest: ApiChatRequest = {
      thread_id: session.threadId || undefined,
      message: null,
      widget_submission: {
        widget_id: widgetId,
        selected_options: selectedOptions.length > 0 ? selectedOptions : undefined,
        button_action: buttonAction,
      },
    };

    const apiResponse = await post<ApiChatResponse>('/api/v1/chat/chat', apiRequest, { timeout: 60000 });

    if (apiResponse.thread_id) {
      session.threadId = apiResponse.thread_id;
    }

    const assistantMessage = this.createAssistantMessage(apiResponse);

    // Add assistant message and update session
    session.messages.push(assistantMessage);
    session.updatedAt = new Date();
    this.updateSessionInStorage(session);

    return { session, message: assistantMessage };
  }

  private async ensureSession(): Promise<ChatSession> {
    let session = await this.getCurrentSession();
    if (!session) {
      session = await this.createSession();
    }
    return session;
  }

  private addUserMessageToSession(
    session: ChatSession,
    content: string,
    context?: MessageContext,
    userMessageId?: string,
  ): void {
    const userMessage: UserMessage = {
      id: userMessageId || this.generateId(),
      content,
      sender: 'user',
      timestamp: new Date(),
      context,
    };
    session.messages.push(userMessage);
  }

  private async tryHandlerProcessing(
    session: ChatSession,
    content: string,
    context?: MessageContext,
  ): Promise<ChatMessage | null> {
    if (!session.handlerType) {
      return null;
    }

    const handler = chatHandlerRegistry.getHandler(session.handlerType as HandlerType);
    if (!handler || !handler.canHandle(session)) {
      return null;
    }

    return await this.handleSpecializedMessage(session, content, context, handler);
  }

  private async processRegularChat(session: ChatSession, content: string, signal?: AbortSignal): Promise<ChatMessage> {
    const apiRequest: ApiChatRequest = {
      thread_id: session.threadId || undefined,
      message: content,
    };

    const apiResponse = await post<ApiChatResponse>('/api/v1/chat/chat', apiRequest, { timeout: 60000, signal });
    // Update session with thread_id if returned
    if (apiResponse.thread_id) {
      session.threadId = apiResponse.thread_id;
    }

    const assistantMessage = this.createAssistantMessage(apiResponse);
    this.finalizeSession(session, assistantMessage, content);

    return assistantMessage;
  }

  private createAssistantMessage(apiResponse: ApiChatResponse): ChatMessage {
    const targetPanelId = mapPanelActionToTargetPanel(apiResponse.panel_action);

    let widgetId: string | undefined;
    let widgetType: string | undefined;
    let widgetData: Record<string, any> | undefined;

    if (apiResponse.widget?.action_type === 'show_widget') {
      const widget = apiResponse.widget.widget;
      widgetId = widget.widget_id;
      widgetType = widget.widget_type;
      widgetData = widget as Record<string, any>;
    }

    // Map API display content to domain display content
    const displayContent = apiResponse.display_content
      ? this.mapApiDisplayContentToDomain(apiResponse.display_content)
      : undefined;

    return {
      id: this.generateId(),
      content: apiResponse.content,
      sender: 'assistant',
      timestamp: new Date(),
      targetPanelId,
      widgetId,
      widgetType,
      widgetData,
      displayContent,
    };
  }

  private mapApiDisplayContentToDomain(apiContent: ApiDisplayContent): DisplayContent {
    return {
      content_type: apiContent.content_type,
      content_data: apiContent.content_data,
      title: apiContent.title,
      header_actions: apiContent.header_actions,
      footer_actions_left: apiContent.footer_actions_left,
      footer_actions_right: apiContent.footer_actions_right,
      footer_actions: apiContent.footer_actions,
    };
  }

  private finalizeSession(session: ChatSession, assistantMessage: ChatMessage, originalContent: string): void {
    // Add assistant message
    session.messages.push(assistantMessage);
    session.updatedAt = new Date();

    // Auto-generate title from first user message
    this.maybeUpdateSessionTitle(session, originalContent);

    // Update session in storage
    this.updateSessionInStorage(session);
  }

  private maybeUpdateSessionTitle(session: ChatSession, content: string): void {
    const userMessageCount = session.messages.filter((m) => m.sender === 'user').length;
    if (userMessageCount === 1 && session.title === 'New Chat') {
      session.title = content.length > 50 ? `${content.substring(0, 47)}...` : content;
    }
  }

  async addSystemMessage(
    content: string,
    threadId?: string,
    handlerType?: HandlerType,
  ): Promise<{ session: ChatSession; message: ChatMessage }> {
    let session = await this.getCurrentSession();
    if (!session) {
      session = await this.createSession();
    }

    // Store the summarisation thread_id separately so it doesn't overwrite the chat's thread_id
    if (threadId) {
      session.summaryThreadId = threadId;
    }
    if (handlerType) {
      session.handlerType = handlerType;
    }

    const systemMessage: ChatMessage = {
      id: this.generateId(),
      content: content,
      sender: 'assistant',
      timestamp: new Date(),
    };

    session.messages.push(systemMessage);
    session.updatedAt = new Date();

    const index = sessions.findIndex((s) => s.id === session.id);
    if (index >= 0) {
      sessions[index] = session;
    }

    return { session, message: systemMessage };
  }

  /**
   * Handle specialized message processing using handler registry
   */
  private async handleSpecializedMessage(
    session: ChatSession,
    content: string,
    context: MessageContext | undefined,
    handler: any,
  ): Promise<ChatMessage> {
    try {
      const result = await handler.handleMessage(content, session, context);

      if (result.handlerResults) {
        if (!session.handlerResults) {
          session.handlerResults = {};
        }
        session.handlerResults[session.handlerType!] = result.handlerResults;
      }

      if (session.handlerResults?.[session.handlerType!]) {
        const shouldTransition = handler.shouldTransitionToGeneral(session.handlerResults[session.handlerType!]);
        if (shouldTransition) {
          session.handlerType = null;
          session.summaryThreadId = null;
        }
      }

      // Create assistant message from handler result
      const assistantMessage: ChatMessage = {
        id: this.generateId(),
        content: result.response,
        sender: 'assistant',
        timestamp: new Date(),
      };

      // Update session with assistant message
      session.messages.push(assistantMessage);
      session.updatedAt = new Date();

      // Update session storage
      this.updateSessionInStorage(session);

      return assistantMessage;
    } catch {
      // Fallback to regular chat if handler fails
      const apiRequest: ApiChatRequest = {
        thread_id: session.threadId || undefined,
        message: content,
      };

      const apiResponse = await post<ApiChatResponse>('/api/v1/chat/chat', apiRequest, { timeout: 60000 });

      const assistantMessage: ChatMessage = {
        id: this.generateId(),
        content: apiResponse.content,
        sender: 'assistant',
        timestamp: new Date(),
        targetPanelId: mapPanelActionToTargetPanel(apiResponse.panel_action),
      };

      session.messages.push(assistantMessage);
      session.updatedAt = new Date();

      this.updateSessionInStorage(session);

      return assistantMessage;
    }
  }

  /**
   * Update session in storage
   */
  private updateSessionInStorage(session: ChatSession): void {
    const index = sessions.findIndex((s) => s.id === session.id);
    if (index >= 0) {
      sessions[index] = session;
    }
  }

  /**
   * Generate generic id
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  async getSessions(): Promise<ChatSession[]> {
    try {
      const apiThreads = await fetcher<ApiThread[]>('/api/v1/chat/threads');

      const apiSessions: ChatSession[] = apiThreads.map((thread) => ({
        id: thread.langgraph_thread_id,
        title: thread.title || 'New Chat',
        messages: [],
        createdAt: new Date(thread.created_at),
        updatedAt: new Date(thread.updated_at),
        isActive: thread.is_active,
        threadId: thread.langgraph_thread_id,
      }));

      // Find the current in-memory session to preserve its messages
      const currentInMemorySession = currentSessionId
        ? sessions.find((s) => s.id === currentSessionId)
        : null;

      if (currentInMemorySession) {
        if (currentInMemorySession.threadId) {
          // Merge ALL runtime fields into the matching API session so nothing is lost
          // (messages, insightsContext, threadId, etc.) — only override server-authoritative
          // metadata such as title/timestamps from the API response.
          const apiIdx = apiSessions.findIndex((s) => s.id === currentInMemorySession.threadId);
          if (apiIdx >= 0) {
            const apiSession = apiSessions[apiIdx];
            apiSessions[apiIdx] = {
              ...currentInMemorySession,
              id: apiSession.id,                                      // langgraph_thread_id
              threadId: apiSession.id,                                // keep in sync
              title: apiSession.title || currentInMemorySession.title,
              createdAt: apiSession.createdAt,
              updatedAt: apiSession.updatedAt,
              isActive: apiSession.isActive,
            };
            // Keep currentSessionId aligned with langgraph_thread_id
            currentSessionId = currentInMemorySession.threadId;
          } else {
            // threadId is set but not listed under /api/v1/chat/threads — e.g. a
            // Robin Chat - Experimental thread, which lives in a different backend
            // thread space than the Nav "AI Chat" threads this list returns.
            // Preserve it as-is instead of silently dropping it (previously this
            // branch did nothing, so the session vanished from `sessions` and the
            // next message would look like a fresh conversation — thread_id reset
            // to undefined even though the backend still had the real thread open).
            apiSessions.unshift(currentInMemorySession);
          }
        } else {
          // New session not yet saved to API — prepend it
          apiSessions.unshift(currentInMemorySession);
        }
      }

      sessions = apiSessions;
      if (sessions.length > MAX_IN_MEMORY_SESSIONS) {
        sessions = sessions.slice(0, MAX_IN_MEMORY_SESSIONS);
      }
      return apiSessions;
    } catch {
      // Fallback to in-memory sessions if API call fails
      return sessions;
    }
  }

  async getCurrentSession(): Promise<ChatSession | null> {
    if (!currentSessionId) return null;
    return sessions.find((s) => s.id === currentSessionId) || null;
  }

  // Persists the right-panel context (workflow_id + checkpoint_id) set before any session exists,
  // so the first created session inherits it without needing a separate flush.
  private pendingInsightsContext: { workflowId: string; checkpointId: string } | null = null;

  setPendingInsightsContext(workflowId: string, checkpointId: string): void {
    this.pendingInsightsContext = { workflowId, checkpointId };
  }

  getPendingInsightsContext(): { workflowId: string; checkpointId: string } | null {
    return this.pendingInsightsContext;
  }

  async createSession(): Promise<ChatSession> {
    const session: ChatSession = {
      id: this.generateId(),
      title: 'New Chat',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
      insightsContext: this.pendingInsightsContext
        ? { workflowId: this.pendingInsightsContext.workflowId, checkpointId: this.pendingInsightsContext.checkpointId }
        : undefined,
    };

    sessions = sessions.filter((s) => s.id !== session.id);
    sessions.unshift(session);
    if (sessions.length > MAX_IN_MEMORY_SESSIONS) {
      sessions = sessions.slice(0, MAX_IN_MEMORY_SESSIONS);
    }
    currentSessionId = session.id;
    return session;
  }

  async switchSession(sessionId: string): Promise<ChatSession> {
    const session = sessions.find((s) => s.id === sessionId);
    if (!session) {
      throw new Error('Session not found');
    }

    // Fetch messages from API when loading a historical session (threadId set but no messages loaded)
    if (session.threadId && session.messages.length === 0) {
      try {
        const apiMessages = await fetcher<ApiThreadMessage[]>(
          `/api/v1/chat/threads/${session.threadId}/messages`,
        );
        const messages: ChatMessage[] = apiMessages.map((m) => ({
          id: m.id || this.generateId(),
          sender: m.role === 'user' ? 'user' : 'assistant',
          content: m.content,
          timestamp: new Date(),
        }));
        const populated = { ...session, messages };
        this.updateSessionInStorage(populated);
        currentSessionId = sessionId;
        return populated;
      } catch {
        // Fall through with empty messages if message fetch fails
      }
    }

    currentSessionId = sessionId;
    return session;
  }

  /** POST /api/v1/chat/threads/{thread_id}/messages/{message_id}/feedback */
  async submitFeedback(
    threadId: string,
    rating: 'positive' | 'negative',
    options?: { comment?: string; messageId?: string; agentId?: string },
  ): Promise<void> {
    const messageId = options?.messageId;
    if (!messageId) return;
    await post(`/api/v1/chat/threads/${threadId}/messages/${messageId}/feedback`, {
      rating,
      comment: options?.comment ?? null,
    });
  }

  async updateSessionTitle(sessionId: string, title: string): Promise<ChatSession> {
    const session = sessions.find((s) => s.id === sessionId);

    if (session?.threadId) {
      await patch(`/api/v1/chat/threads/${session.threadId}`, { title });
    }

    if (session) {
      session.title = title;
      session.updatedAt = new Date();

      const index = sessions.findIndex((s) => s.id === sessionId);
      sessions[index] = session;

      return session;
    }

    return { id: sessionId, title, messages: [], createdAt: new Date(), updatedAt: new Date(), isActive: false };
  }

  /** Sync an externally-updated session into the service internal state (used by streaming hook). */
  syncSession(session: ChatSession): void {
    const index = sessions.findIndex((s) => s.id === session.id);
    if (index >= 0) {
      sessions[index] = session;
    } else {
      sessions.unshift(session);
      currentSessionId = session.id;
    }
  }

  /** Public wrapper around ensureSession for the streaming hook. */
  async getOrCreateSession(): Promise<ChatSession> {
    return this.ensureSession();
  }

  /** Fetch the latest checkpoint_id for an Insights Hub workflow. */
  async resolveCheckpointId(workflowId: string): Promise<string | undefined> {
    try {
      const dto = await fetcher<{ checkpoint_id?: string | null }>(
        `/api/v1/insights-hub/${encodeURIComponent(workflowId)}`,
      );
      return dto.checkpoint_id ?? undefined;
    } catch {
      return undefined;
    }
  }

  async deleteSession(sessionId: string): Promise<void> {
    const session = sessions.find((s) => s.id === sessionId);

    if (session?.threadId) {
      await del(`/api/v1/chat/threads/${session.threadId}`);
    }

    const index = sessions.findIndex((s) => s.id === sessionId);
    if (index >= 0) {
      sessions.splice(index, 1);
    }

    if (currentSessionId === sessionId) {
      currentSessionId = sessions.length > 0 ? sessions[0].id : null;
    }
  }
}

export const chatApiService = new ChatApiService();

// =============================================================================
// SSE Streaming — /api/v1/chat/chat-stream & /api/v1/chat/resume-stream
// =============================================================================

const BASE_URL = environment.VITE_APP_API_BASE_URL;

function tryParseJsonDetail(body: string): string {
  try {
    const json = JSON.parse(body);
    return typeof json.detail === 'string' ? json.detail : '';
  } catch {
    return '';
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const [token, graphToken] = await Promise.all([getAccessToken(), getGraphToken()]);

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  if (token) headers['Authorization'] = `Bearer ${token}`;
  if (graphToken) headers['X-MS-Graph-Token'] = graphToken;

  return headers;
}

function dispatchNavEvent(event: NavStreamEvent, handlers: NavStreamEventHandlers): void {
  // Robin Chat - Experimental sends 'tool' events (tool call lifecycle) that
  // aren't part of NavStreamEvent's type union; no handler surfaces them in
  // the UI yet, so drop silently instead of logging a misleading "unknown"
  // warning for an expected, documented type. Checked before the switch so
  // TS's exhaustive narrowing on the real union isn't disturbed.
  if ((event as { type: string }).type === 'tool') return;

  switch (event.type) {
    case 'start':     handlers.onStart?.(event);     break;
    case 'state':     handlers.onState?.(event);     break;
    case 'reasoning': handlers.onReasoning?.(event); break;
    case 'content':   handlers.onContent?.(event);   break;
    case 'widget':    handlers.onWidget?.(event);    break;
    case 'navigate':  handlers.onNavigate?.(event);  break;
    case 'display':          handlers.onDisplay?.(event);         break;
    case 'content_summary':  handlers.onContentSummary?.(event);  break;
    case 'thread_summary':   handlers.onThreadSummary?.(event);   break;
    case 'error':            handlers.onError?.(event);           break;
    case 'done':             handlers.onDone?.(event);            break;
    case 'notice':    handlers.onNotice?.(event);    break;
    case 'suggested_actions': handlers.onSuggestedActions?.(event); break;
    case 'insights_context':  handlers.onInsightsContext?.(event);  break;
    case 'report':             handlers.onReport?.(event);            break;
    default:
      console.warn('Unknown SSE event type:', (event as { type: string }).type);
  }
}

interface SSEParseState {
  eventType: string;
  data: string;
}

function tryDispatchSSEEvent(state: SSEParseState, handlers: NavStreamEventHandlers): SSEParseState {
  if (!state.data) return state;
  try {
    const parsed = JSON.parse(state.data);
    if (!parsed.type && state.eventType) parsed.type = state.eventType;
    dispatchNavEvent(parsed as NavStreamEvent, handlers);
  } catch (err) {
    console.error('Failed to parse SSE data:', state.data, err);
  }
  return { eventType: '', data: '' };
}

function processSSELine(line: string, state: SSEParseState): SSEParseState {
  if (line.startsWith('event:')) return { ...state, eventType: line.slice(6).trim() };
  if (line.startsWith('data:')) return { ...state, data: line.slice(5).trim() };
  return state;
}

async function parseNavSSEStream(
  reader: ReadableStreamDefaultReader<Uint8Array>,
  handlers: NavStreamEventHandlers,
): Promise<void> {
  const decoder = new TextDecoder();
  let buffer = '';
  let state: SSEParseState = { eventType: '', data: '' };

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        if (line === '') {
          state = tryDispatchSSEEvent(state, handlers);
        } else {
          state = processSSELine(line, state);
        }
      }
    }

    tryDispatchSSEEvent(state, handlers);
  } finally {
    reader.cancel().catch(() => {});
  }
}

export async function streamChat(
  request: NavChatStreamRequest,
  handlers: NavStreamEventHandlers,
  signal?: AbortSignal,
  endpoint = '/api/v1/chat/chat-stream',
): Promise<void> {
  const url = `${BASE_URL}${endpoint}`;
  try {
    const headers = await getAuthHeaders();
    headers['Accept'] = 'text/event-stream';

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      const parsed = detail ? tryParseJsonDetail(detail) : '';
      throw new Error(parsed || `Chat request failed (${response.status})`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('ReadableStream not supported');

    await parseNavSSEStream(reader, handlers);
  } catch (error) {
    console.error('[streamChat] error', error);
    if (error instanceof Error) {
      if (error.name === 'AbortError') return;
      handlers.onStreamError?.(error);
    } else {
      handlers.onStreamError?.(new Error('Unknown error during streaming'));
    }
  }
}

export async function resumeNavStream(
  request: NavResumeStreamRequest,
  handlers: NavStreamEventHandlers,
  signal?: AbortSignal,
): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    headers['Accept'] = 'text/event-stream';

    const response = await fetch(`${BASE_URL}/api/v1/chat/resume-stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify(request),
      signal,
    });

    if (!response.ok) {
      const detail = await response.text().catch(() => '');
      const parsed = detail ? tryParseJsonDetail(detail) : '';
      throw new Error(parsed || `Chat request failed (${response.status})`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('ReadableStream not supported');

    await parseNavSSEStream(reader, handlers);
  } catch (error) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') return;
      handlers.onStreamError?.(error);
    } else {
      handlers.onStreamError?.(new Error('Unknown error during resume'));
    }
  }
}

// =============================================================================
// Cancel & restore-checkpoint — no equivalent exists under /api/v1/chat/*, so
// these call the "Robin Chat - Experimental" backend (/api/v1/robin-chat/*)
// even though chat-stream/resume-stream stay on /api/v1/chat/*. Best-effort:
// the thread_id came from a /chat/chat-stream session, not a robin-chat one,
// so the backend may not recognize it (different agent/thread namespace) —
// callers should treat failures here as non-fatal.
// =============================================================================

export interface CancelStreamResponse {
  success: boolean;
  message: string;
}

export interface RestoreCheckpointResponse {
  success: boolean;
  message: string;
  restored_checkpoint_id?: string;
}

export async function cancelStream(threadId: string, runId: string): Promise<CancelStreamResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${BASE_URL}/api/v1/robin-chat/cancel`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ thread_id: threadId, run_id: runId }),
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

  return response.json();
}

export async function restoreCheckpoint(threadId: string, checkpointId: string): Promise<RestoreCheckpointResponse> {
  const headers = await getAuthHeaders();

  const response = await fetch(`${BASE_URL}/api/v1/robin-chat/restore`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ thread_id: threadId, checkpoint_id: checkpointId }),
  });

  if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);

  return response.json();
}
